import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useSearchParams as useUrlSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { formatLocalDate } from "../utils/dateUtils";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BannerSearch from "../components/BannerSearch";
import BreadcrumbFilterHeader from "../components/BreadcrumbPropertyList";
import FilterSidebar from "../components/FilterSidebar";
import PropertyCard from "../components/PropertyCard";
import PageMeta from "../components/PageMeta";
import JsonLd from "../components/JsonLd";
import API_BASE_URL from "../../src/config";
import "../css/property-list.css";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.guestlinkup.com/" },
    { "@type": "ListItem", position: 2, name: "Stays", item: "https://www.guestlinkup.com/stays" },
  ],
};

const PropertyList = () => {
  const { state } = useLocation(); // 👈 get formData passed from BannerSearch
  const [urlParams] = useUrlSearchParams(); // for direct/shareable links, e.g. ?promo=FIRST50
  const promoFromUrl = urlParams.get("promo");
  const locationFromUrl = urlParams.get("location"); // e.g. ?location=Darjeeling — marketing CTA links
  const [filteredStays, setFilteredStays] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 0]);
  // Starts true only when a real search is actually about to run (the
  // effect below will fire one), so the first paint shows a spinner instead
  // of "No Home Stays found" flashing before the fetch is even dispatched.
  // A bare /stays visit with nothing to search for yet correctly stays
  // non-loading rather than spinning forever.
  const [loading, setLoading] = useState(() => Boolean(state || promoFromUrl || locationFromUrl));
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchParams, setSearchParams] = useState({});
  // Client-side only refinement — narrows whatever the main search already
  // returned, never triggers a new stays/list request. Kept fully separate
  // from activeFilters/handleSearch so core search stays untouched.
  const [propertyTypeFilter, setPropertyTypeFilter] = useState([]);
  const displayedStays =
    propertyTypeFilter.length === 0
      ? filteredStays
      : filteredStays.filter((s) => propertyTypeFilter.includes(s.property_type));

  // console.log(priceRange)

  useEffect(() => {
    if (filterOpen) {
      document.body.classList.add("filter-layout");
    } else {
      document.body.classList.remove("filter-layout");
    }

    return () => {
      document.body.classList.remove("filter-layout");
    };
  }, [filterOpen]);

  // 🔎 API call to fetch stays
  // const handleSearch = async (params) => {
  //   setLoading(true);
  //   try {
  //     const response = await axios.post(
  //       `${API_BASE_URL}stays/list`,
  //       {
  //         page: 1,
  //         limit: 50,
  //         ...params, // merge params from BannerSearch / filters

  //       }
  //     );

  //     console.log("API response:", response.data);

  //     if (response.data.success) {
  //       setFilteredStays(response.data.data || []);
  //     } else {
  //       setFilteredStays([]);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching stays:", err);
  //     setFilteredStays([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleSearch = async (params = {}) => {
  //   setLoading(true);

  //   try {
  //     const finalParams = {
  //       page: 1,
  //       limit: 50,
  //       ...activeFilters, //. keep previous filters
  //       ...params,        //. override if needed
  //     };

  //     const response = await axios.post(
  //       `${API_BASE_URL}stays/list`,
  //       finalParams
  //     );

  //     if (response.data.success) {
  //       setFilteredStays(response.data.data || []);
  //     } else {
  //       setFilteredStays([]);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching stays:", err);
  //     setFilteredStays([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
  if (state) {
    setSearchParams(state);
    setActiveFilters(state);

    handleSearch(state);
    return;
  }

  const runSearch = (derived) => {
    setSearchParams(derived);
    setActiveFilters(derived);
    handleSearch(derived);
  };

  if (locationFromUrl) {
    // Location CTA link (marketing campaigns) — e.g. /stays?location=Darjeeling,
    // optionally combined with ?promo=. Deliberately sends NO dates: stays/list
    // treats a dateless request as "browse this location" and returns every
    // configured property instead of hard-filtering to specific nights, since
    // a marketing click has no trip planned yet. The user picks real dates via
    // the search bar afterward, which re-runs a normal date-checked search
    // through the `state` branch above. location is a human-readable name
    // (matches BannerSearch's own case-insensitive lookup), resolved here to
    // the destination_id stays/list actually filters on — the API has no
    // name-based filter, only destination_id.
    const browseParams = {
      page: 1,
      limit: 10,
      ...(promoFromUrl && {
        promo_codes: promoFromUrl.split(",").map((c) => c.trim()).filter(Boolean),
      }),
    };

    axios
      .get(`${API_BASE_URL}home/locations`)
      .then((res) => {
        const match = (res.data.data || []).find(
          (loc) => loc.location_name.toLowerCase() === locationFromUrl.toLowerCase()
        );
        runSearch({
          ...browseParams,
          ...(match && { destination_id: match.id, destination_name: match.location_name }),
        });
      })
      .catch(() => runSearch(browseParams));
    return;
  }

  if (promoFromUrl) {
    // Direct/shareable link (marketing callouts, ads) — e.g. /stays?promo=FIRST50
    // or /stays?promo=FIRST50,WEEKEND15 — with no navigation state to read,
    // build the same default search Book Now uses.
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date();
    dayAfter.setDate(dayAfter.getDate() + 2);

    runSearch({
      page: 1,
      limit: 10,
      checkin_date: formatLocalDate(tomorrow),
      checkout_date: formatLocalDate(dayAfter),
      adults: 1,
      kids: 0,
      rooms_required: 1,
      promo_codes: promoFromUrl.split(",").map((c) => c.trim()).filter(Boolean),
    });
  }
}, [state, promoFromUrl, locationFromUrl]);

const handleSearch = async (params = {}) => {
  setLoading(true);

  try {
    const normalizedParams = {
      ...params,
      kids: params.kids === "" ? 0 : params.kids,
    };

    const finalParams = {
      page: 1,
      limit: 50,
      ...normalizedParams,
    };

    const response = await axios.post(
      `${API_BASE_URL}stays/list`,
      finalParams
    );

    if (response.data.success) {
      setFilteredStays(response.data.data || []);

      //. reset slider only on NEW SEARCH (no filters)
      if (!params.min_price && !params.max_price) {
        setPriceRange([
          response.data.price_range?.min_price || 0,
          response.data.price_range?.max_price || 0,
        ]);
      }

      if (typeof window.fbq === "function") {
        window.fbq("track", "Search", {
          search_string: params.destination_name || params.keyword || "",
          content_category: "stay",
          num_results: (response.data.data || []).length,
        });
      }
    } else {
      setFilteredStays([]);
    }
  } catch (err) {
    console.error("Error fetching stays:", err);
    setFilteredStays([]);
  } finally {
    setLoading(false);
  }
};

  // useEffect(() => {
  //   if (state) {
  //     setSearchParams(state);
  //     handleSearch(state);
  //   }
  // }, [state]);

  const handleFilter = (filters) => {
  const updated = {
    ...searchParams,
    ...activeFilters,
    ...filters,
  };

  setActiveFilters(updated);

  handleSearch(updated); //. always send full payload
};

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <PageMeta
        title="Browse Homestays - GuestLinkup"
        description="Browse authentic family-run homestays across Darjeeling, Sikkim, Gangtok, Kalimpong, and the Himalayan Northeast."
      />
      <JsonLd id="breadcrumb" schema={breadcrumbSchema} />
      {/* Header */}
      <Header />

      {/* Banner Search stays visible here */}
      <BannerSearch />
      {filterOpen && (
        <div
          className="filter-ovarlay"
          onClick={() => setFilterOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <section className="propertycontainer">
        <div className="container">
          {/* Breadcrumb and Filters */}
          {/* <BreadcrumbFilterHeader /> */}
          {/* <BreadcrumbFilterHeader totalProperties={filteredStays.length} /> */}
          {/* <BreadcrumbFilterHeader 
  totalProperties={filteredStays.length} 
  locationName={searchParams?.destination_name || ""} 
/> */}

          <BreadcrumbFilterHeader
            totalProperties={displayedStays.length}
            locationName={searchParams?.destination_name || ""}
            onFilter={handleFilter}
            onToggleSidebar={() => setFilterOpen(true)}
          />



          {/* onFilter={handleFilter}  */}
          <div className="row flex-nowrap">
            <FilterSidebar
              priceRange={priceRange}
              onFilter={handleFilter}
              onPropertyTypeFilter={setPropertyTypeFilter}
              initialPromoCodes={searchParams?.promo_codes}
            />

            {/* Property Cards */}
            {/* <div className="flex-fill">
              <div className="propertylistouter">
                {loading ? (
                  <div className="text-center p-5">Loading...</div>
                ) : filteredStays.length > 0 ? (
                  filteredStays.map((stay) => (
                    <PropertyCard key={stay.id} stay={stay} />
                  ))
                ) : (
                  <div className="text-center p-5">No stays found.</div>
                )}

              </div>

            </div> */}

            <div className="flex-fill">
              <div className="propertylistouter">
                {loading ? (
                  <div className="text-center p-5 d-flex justify-content-center">
                    <ClipLoader size={45} />
                  </div>
                ) : displayedStays.length > 0 ? (
                  displayedStays.map((stay) => (
                    <PropertyCard key={stay.id} stay={stay} />
                  ))
                ) : (
                  <div className="text-center p-5 bg-light rounded shadow-sm">

                    <h5 className="mb-2 text-muted">No Home Stays found</h5>
                    <p className="text-secondary mb-0">
                      Try adjusting your filters or search criteria to see more options.
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PropertyList;
